// Focus: a diagram all about one entity. `focusGraph` picks what to show: the entity with every
// property, and what it relates to (a few steps out, or everything connected) as the property mode
// says (all, keys and the rows lines attach to, or names). Any layout can arrange that; `focusArrangement` is the
// one made for it: what the entity points at (principals, its base type) on one side, what points at
// it (dependents, derived types, many-to-many partners) on the other, one column (or row) per step.
import { sizeNode, type EntityNodeData, type Graph, type GraphEdge, type Line, type Measure, type PropertyMode } from './graph';
import type { FlowKind, Positions, SpacingScale } from './layouts';

/** Steps around the focused entity; 'all' is everything connected to it. */
export type FocusDepth = 1 | 2 | 3 | 'all';

export interface FocusGraph {
  graph: Graph;
  /** Step and side of each node: negative it is pointed at, positive it points at the focus, 0 the focus. */
  columns: Map<string, number>;
  /** The order nodes were reached in, for stable arrangements. */
  order: Map<string, number>;
}

/** Which side `other` goes on, seen from `from` across `edge`: -1 what `from` points at, +1 what points at it. */
function sideOf(edge: GraphEdge, from: string): -1 | 1 {
  if (edge.kind === 'manyToMany') return 1;
  // Code map lines flow the other way: callers before what they call.
  if (edge.kind === 'call' || edge.kind === 'uses') return edge.source === from ? 1 : -1;
  // Relationship: source is the dependent. Inheritance: source is the derived type.
  return edge.source === from ? -1 : 1;
}

/**
 * `graph` should show every property (the focused card keeps them all); `mode` is the property mode
 * for the other cards.
 */
export function focusGraph(graph: Graph, center: string, depth: FocusDepth, mode: PropertyMode, measure?: Measure): FocusGraph | null {
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  if (!byId.has(center)) return null;
  const limit = depth === 'all' ? Infinity : depth;

  // Breadth first from the center; a node keeps the side it was first reached on.
  const column = new Map<string, number>([[center, 0]]);
  const order = new Map<string, number>([[center, 0]]);
  let frontier = [center];
  for (let step = 1; step <= limit && frontier.length; step++) {
    const next: string[] = [];
    for (const from of frontier) {
      const fromSide = Math.sign(column.get(from)!);
      for (const e of graph.edges) {
        const other = e.source === from ? e.target : e.target === from ? e.source : null;
        if (!other || other === from || column.has(other) || !byId.has(other)) continue;
        const side = fromSide === 0 ? sideOf(e, from) : fromSide;
        column.set(other, side * step);
        order.set(other, order.size);
        next.push(other);
      }
    }
    frontier = next;
  }

  const edges = graph.edges.filter((e) => column.has(e.source) && column.has(e.target));
  const nodes = graph.nodes.filter((n) => column.has(n.id));
  return { graph: trimCards(nodes, edges, center, mode, measure), columns: column, order };
}

/**
 * The cards of a focus: the focused one as it is, the others with the rows the mode keeps (the keys
 * mode keeps the rows lines attach to). A line whose row is not shown attaches to the card instead.
 */
export function trimCards(cards: EntityNodeData[], edges: GraphEdge[], center: string, mode: PropertyMode, measure?: Measure): Graph {
  const used = new Map<string, Set<string>>();
  const use = (node: string, row: string | null) => {
    if (row) used.set(node, (used.get(node) ?? new Set()).add(row));
  };
  for (const e of edges) {
    use(e.source, e.sourceRow);
    use(e.target, e.targetRow);
  }
  // Keys: the keys, foreign keys and the rows lines attach to, like the keys mode elsewhere.
  const keep = (l: Line, rows: Set<string>) => mode === 'all' || (mode === 'keys' && l.kind === 'row' && (l.row.pk || l.row.fk || l.row.ak || rows.has(l.row.id)));
  const nodes: EntityNodeData[] = cards.map((n) => {
    if (n.id === center) return n;
    const rows = used.get(n.id) ?? new Set<string>();
    const lines = n.lines.filter((l) => keep(l, rows));
    const shown = new Set(lines.flatMap((l) => (l.kind === 'row' ? [l.row.id] : [])));
    return sizeNode({ ...n, lines, anchors: n.anchors.filter((a) => rows.has(a) && shown.has(a)) }, measure);
  });
  const rowsOf = new Map(nodes.map((n) => [n.id, new Set(n.lines.flatMap((l) => (l.kind === 'row' ? [l.row.id] : [])))]));
  const attached = edges.map((e) => {
    const sourceRow = e.sourceRow && rowsOf.get(e.source)!.has(e.sourceRow) ? e.sourceRow : null;
    const targetRow = e.targetRow && rowsOf.get(e.target)!.has(e.targetRow) ? e.targetRow : null;
    return sourceRow === e.sourceRow && targetRow === e.targetRow ? e : { ...e, sourceRow, targetRow };
  });
  return { nodes, edges: attached };
}

const COLUMN_GAP = 150;
const ROW_GAP = 28;
/** Cards per column (or row) before it wraps into another one further out. */
const MAX_PER_COLUMN = 7;
const MAX_PER_ROW = 5;

/**
 * The focus arrangement, flowing like the layout of the same name: left to right puts what the focus
 * points at on the left and what points at it on the right; right to left the other way round; top
 * down and bottom up put them above and below.
 */
export function focusArrangement(view: FocusGraph, center: string, direction: FlowKind, scale: SpacingScale = { nodes: 1, layers: 1 }): Positions {
  const sized = new Map(view.graph.nodes.map((n) => [n.id, n]));
  const vertical = direction === 'down' || direction === 'up';
  const reversed = direction === 'left' || direction === 'up';
  // Along the main axis cards stack; across it columns (or rows) step away from the center.
  const along = (n: EntityNodeData) => (vertical ? n.width : n.height);
  const across = (n: EntityNodeData) => (vertical ? n.height : n.width);
  const alongGap = (vertical ? 40 : ROW_GAP) * scale.nodes;
  const acrossGap = (vertical ? 90 : COLUMN_GAP) * scale.layers;
  const perLine = vertical ? MAX_PER_ROW : MAX_PER_COLUMN;

  const groups = new Map<number, string[]>();
  for (const id of [...view.columns.keys()].sort((a, b) => view.order.get(a)! - view.order.get(b)!)) {
    const c = view.columns.get(id)!;
    groups.set(c, [...(groups.get(c) ?? []), id]);
  }
  const lines: { side: number; ids: string[] }[] = [];
  for (const c of [...groups.keys()].sort((a, b) => Math.abs(a) - Math.abs(b) || a - b)) {
    const ids = groups.get(c)!;
    for (let i = 0; i < ids.length; i += perLine) lines.push({ side: Math.sign(c), ids: ids.slice(i, i + perLine) });
  }

  // Coordinates in (along, across); swapped to (x, y) at the end for the vertical arrangement.
  const at = new Map<string, { a: number; c: number }>();
  const middle = sized.get(center)!;
  const centerAlong = along(middle) / 2;
  let after = across(middle);
  let before = 0;
  for (const { side, ids } of lines) {
    if (side === 0) {
      at.set(center, { a: 0, c: 0 });
      continue;
    }
    const thickness = Math.max(...ids.map((id) => across(sized.get(id)!)));
    const length = ids.reduce((sum, id) => sum + along(sized.get(id)!), 0) + alongGap * (ids.length - 1);
    let a = centerAlong - length / 2;
    const c0 = side > 0 ? after + acrossGap : before - acrossGap - thickness;
    for (const id of ids) {
      const n = sized.get(id)!;
      // Cards lean towards the center: lines before it align their far edge with it.
      at.set(id, { a, c: side > 0 ? c0 : c0 + thickness - across(n) });
      a += along(n) + alongGap;
    }
    if (side > 0) after = c0 + thickness;
    else before = c0;
  }
  const positions: Positions = {};
  for (const [id, p] of at) {
    // Reversed: mirrored around the focused card, which stays where it is.
    const c = reversed ? across(middle) - p.c - across(sized.get(id)!) : p.c;
    positions[id] = vertical ? { x: p.a, y: c } : { x: c, y: p.a };
  }
  return positions;
}
